import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useLanguageStore } from '../../store/languageStore';
import translations from '../../translations';
import { getWorkOrders as getOnlineWorkOrders, getExpenses as getOnlineExpenses, getShifts as getOnlineShifts } from '../../utils/api';
import { getWorkOrders as getOfflineWorkOrders, getExpenses as getOfflineExpenses, getShifts as getOfflineShifts } from '../../utils/offlineDb';
import { WorkOrder, Task } from '../../types/workOrders';
import { Expense } from '../../types/expenses';
import { WorkerShift } from '../../types/auth';
import { format, subDays } from 'date-fns';
import { LoadingSkeleton } from '../common/LoadingSkeleton';
import { ErrorState } from '../common/ErrorState';
import { MetricCard } from './MetricCard';
import { TaskSummary } from './TaskSummary';
import { RecentExpenses } from './RecentExpenses';
import { RecentShifts } from './RecentShifts';
import { Briefcase, CheckCircle2, Clock, DollarSign } from 'lucide-react';

export function DashboardView() {
  const navigate = useNavigate();
  const { accessToken, workerId, workerName, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [shifts, setShifts] = useState<WorkerShift[]>([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const { language } = useLanguageStore();
  const t = translations[language];

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const fetchDashboardData = async () => {
    if (!accessToken || !workerId) {
      setError('Please log in to view dashboard');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      let orders, expenses, shifts;

      if (!navigator.onLine) {
        // Get data from offline storage
        orders = await getOfflineWorkOrders();
        expenses = await getOfflineExpenses();
        shifts = await getOfflineShifts();
      } else {
        // Get data from API
        const [ordersResponse, expensesResponse, shiftsResponse] = await Promise.all([
          getOnlineWorkOrders(accessToken, workerId),
          getOnlineExpenses(accessToken),
          getOnlineShifts(accessToken)
        ]);
        orders = ordersResponse.orders;
        expenses = expensesResponse.expenses;
        shifts = shiftsResponse;
      }

      setWorkOrders(orders);
      
      const workerExpenses = expenses.filter(
        exp => exp.worker && exp.worker.id === parseInt(workerId, 10)
      );
      setExpenses(workerExpenses);
      
      const workerShifts = shifts.filter(
        shift => shift.worker_id === parseInt(workerId, 10)
      );
      setShifts(workerShifts);
      
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && workerId) {
      fetchDashboardData();
    }
  }, [accessToken, workerId]);

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <ErrorState message={error} onRetry={fetchDashboardData} />
      </div>
    );
  }

  const activeOrders = workOrders.filter(
    order => !['COMPLETED', 'REJECTED'].includes(order.status)
  );
  
  const allTasks = activeOrders.flatMap(order => 
    order.Tasks.filter(task => task.isVisible)
  );
  
  const completedTasks = allTasks.filter(task => task.isCompleted);
  const pendingTasks = allTasks.filter(task => !task.isCompleted);

  const threeDaysAgo = subDays(new Date(), 3);
  const recentExpenses = expenses
    .filter(expense => new Date(expense.createdAt) >= threeDaysAgo)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const recentShifts = shifts
    .filter(shift => (shift.clockin_time * 1000) >= threeDaysAgo.getTime())
    .sort((a, b) => b.clockin_time - a.clockin_time);

  return (
    <div className="max-w-7xl mx-auto p-4">
      {isOffline && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg flex items-center gap-2">
          <span className="text-sm">{t.dashboard.offline.message}</span>
        </div>
      )}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t.dashboard.welcome.replace('{name}', workerName || '')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t.dashboard.overview}
        </p>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title={t.dashboard.metrics.activeOrders}
          value={activeOrders.length.toString()}
          icon={<Briefcase className="h-5 w-5" />}
          color="blue"
        />
        <MetricCard
          title={t.dashboard.metrics.completedTasks}
          value={completedTasks.length.toString()}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="green"
        />
        <MetricCard
          title={t.dashboard.metrics.pendingTasks}
          value={pendingTasks.length.toString()}
          icon={<Clock className="h-5 w-5" />}
          color="yellow"
        />
        <MetricCard
          title={t.dashboard.metrics.recentExpenses}
          value={recentExpenses.length.toString()}
          icon={<DollarSign className="h-5 w-5" />}
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Summary */}
        <TaskSummary tasks={allTasks} />

        {/* Recent Activity */}
        <div className="space-y-6">
          <RecentExpenses expenses={recentExpenses} />
          <RecentShifts shifts={recentShifts} />
        </div>
      </div>
    </div>
  );
}